import React, { useState, useEffect } from 'react';
import {
    Container,
    Header,
    SpaceBetween,
    Form,
    FormField,
    Input,
    Button,
    Alert,
    Box,
    ColumnLayout,
    KeyValuePairs,
    Textarea,
    Modal,
    Select,
    SelectProps,
    ExpandableSection
} from '@cloudscape-design/components';
import { userCallbackURLApi } from '../../client/clients';
import { UserCallbackUrlRequest, UserCallbackUrlResponse, ArticleProcessingRequest, ArticleProcessingResponse } from '../../api/models';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export function CallbackUrlManagementPage() {
    useDocumentTitle('Webhook notifications');
    const [config, setConfig] = useState<UserCallbackUrlResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    
    // Form state
    const [url, setUrl] = useState('');
    const [headersJson, setHeadersJson] = useState('');
    const [headersError, setHeadersError] = useState<string | null>(null);
    
    // Test functionality state
    const [testArticleId, setTestArticleId] = useState('');
    const [testCredentialKey, setTestCredentialKey] = useState<SelectProps.Option | null>(null);
    const [testingArticle, setTestingArticle] = useState(false);
    const [sendingToCallback, setSendingToCallback] = useState(false);
    const [testResult, setTestResult] = useState<ArticleProcessingResponse | null>(null);
    const [testAlert, setTestAlert] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
    
    const credentialKeyOptions: SelectProps.Option[] = [
        // F2: user-facing credential names.
        { label: 'SeekingAlpha Pro', value: 'SeekingAlphaProCookie' },
        { label: 'SeekingAlpha Premium', value: 'SeekingAlphaPremiumCookie' }
    ];

    // Load existing configuration
    useEffect(() => {
        loadConfiguration();
    }, []);

    const loadConfiguration = async () => {
        setLoading(true);
        setAlert(null);
        try {
            const response = await userCallbackURLApi.getCurrent();
            const data = response.data;
            setConfig(data);
            
            if (data) {
                setUrl(data.url || '');
                setHeadersJson(data.headers ? JSON.stringify(data.headers, null, 2) : '');
            } else {
                setUrl('');
                setHeadersJson('');
            }
        } catch (error: any) {
            if (error?.response?.status === 404) {
                setConfig(null);
                setUrl('');
                setHeadersJson('');
            } else {
                console.error('Error loading callback URL configuration:', error);
                setAlert({ 
                    type: 'error', 
                    message: 'Failed to load callback URL configuration: ' + (error.message || 'Unknown error')
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const validateAndParseHeaders = (headersStr: string): Record<string, string> | null => {
        if (!headersStr.trim()) {
            setHeadersError(null);
            return {};
        }

        try {
            const parsed = JSON.parse(headersStr);

            if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
                setHeadersError('Headers must be a JSON object, e.g. {"Authorization": "Bearer token"}.');
                return null;
            }

            for (const [key, value] of Object.entries(parsed)) {
                if (typeof value !== 'string') {
                    setHeadersError(`Header "${key}" value must be a string.`);
                    return null;
                }
            }

            setHeadersError(null);
            return parsed as Record<string, string>;
        } catch (error: any) {
            // J2: surface the parser's own message so users can locate the bad token.
            const parserMessage = error?.message || 'Invalid JSON';
            setHeadersError(`Invalid JSON: ${parserMessage}`);
            return null;
        }
    };

    const handleSave = async () => {
        setAlert(null);
        
        // Validate URL
        if (!url.trim()) {
            setAlert({ type: 'error', message: 'Please enter a callback URL' });
            return;
        }
        
        // Validate URL format
        try {
            new URL(url);
        } catch (error) {
            setAlert({ type: 'error', message: 'Invalid URL format' });
            return;
        }
        
        // Validate and parse headers
        const headers = validateAndParseHeaders(headersJson);
        if (headersError || headers === null) {
            return;
        }
        
        setSaving(true);
        try {
            const request: UserCallbackUrlRequest = {
                url: url.trim(),
                headers: Object.keys(headers).length > 0 ? headers : undefined
            };
            
            const response = await userCallbackURLApi.upsert(request);
            setConfig(response.data);
            setAlert({ type: 'success', message: 'Webhook configuration saved successfully.' });
        } catch (error: any) {
            console.error('Error saving callback URL:', error);
            setAlert({
                type: 'error',
                message: 'Failed to save webhook URL: ' + (error.response?.data?.message || error.message || 'Unknown error')
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        setAlert(null);
        try {
            await userCallbackURLApi._delete();
            setConfig(null);
            setUrl('');
            setHeadersJson('');
            setAlert({ type: 'success', message: 'Webhook configuration deleted successfully.' });
            setDeleteModalVisible(false);
        } catch (error: any) {
            console.error('Error deleting callback URL:', error);
            setAlert({
                type: 'error',
                message: 'Failed to delete webhook URL: ' + (error.response?.data?.message || error.message || 'Unknown error')
            });
        } finally {
            setDeleting(false);
        }
    };

    const handleHeadersChange = (value: string) => {
        setHeadersJson(value);
        // Clear error when user starts typing
        if (headersError) {
            setHeadersError(null);
        }
    };

    const handleTestArticle = async () => {
        if (!testArticleId || !testCredentialKey) {
            setTestAlert({ type: 'error', message: 'Please provide both Article ID and Credential' });
            return;
        }

        setTestingArticle(true);
        setTestAlert(null);
        setTestResult(null);

        try {
            const request: ArticleProcessingRequest = {
                articleId: testArticleId,
                credentialKey: testCredentialKey.value as any
            };

            const response = await userCallbackURLApi.sendTestArticle(request);
            setTestResult(response.data);
            setTestAlert({ type: 'success', message: 'Article processed successfully!' });
        } catch (error: any) {
            console.error('Error testing article:', error);
            setTestAlert({
                type: 'error',
                message: 'Failed to process article: ' + (error.response?.data?.message || error.message || 'Unknown error')
            });
        } finally {
            setTestingArticle(false);
        }
    };

    const handleSendToCallback = async () => {
        if (!testArticleId || !testCredentialKey) {
            setTestAlert({ type: 'error', message: 'Please provide both Article ID and Credential' });
            return;
        }

        if (!config?.url) {
            setTestAlert({ type: 'error', message: 'No webhook URL configured. Please save a webhook URL first.' });
            return;
        }

        setSendingToCallback(true);
        setTestAlert(null);
        setTestResult(null);

        try {
            const request: ArticleProcessingRequest = {
                articleId: testArticleId,
                credentialKey: testCredentialKey.value as any
            };

            await userCallbackURLApi.processArticleAndCallback(request);
            setTestAlert({
                type: 'success',
                message: `Article processed and sent to webhook: ${config.url}`
            });
        } catch (error: any) {
            console.error('Error sending to callback:', error);
            setTestAlert({
                type: 'error',
                message: 'Failed to send to webhook: ' + (error.response?.data?.message || error.message || 'Unknown error')
            });
        } finally {
            setSendingToCallback(false);
        }
    };

    return (
        <Container>
            <SpaceBetween size="l">
                <Header
                    variant="h1"
                    description="Configure a webhook URL to receive notifications when trading actions occur. You can optionally add custom headers for authentication."
                    actions={
                        <Button
                            iconName="refresh"
                            loading={loading}
                            onClick={loadConfiguration}
                        >
                            Refresh
                        </Button>
                    }
                >
                    Webhook notifications
                </Header>

                {alert && (
                    <Alert
                        type={alert.type}
                        dismissible
                        onDismiss={() => setAlert(null)}
                    >
                        {alert.message}
                    </Alert>
                )}

                {config && (
                    <Box>
                        <Header variant="h2">Current configuration</Header>
                        <ColumnLayout columns={1} variant="text-grid">
                            <KeyValuePairs
                                columns={1}
                                items={[
                                    {
                                        label: 'Email',
                                        value: config.email
                                    },
                                    {
                                        label: 'Webhook URL',
                                        value: config.url
                                    },
                                    {
                                        label: 'Headers',
                                        value: config.headers && Object.keys(config.headers).length > 0
                                            ? JSON.stringify(config.headers, null, 2)
                                            : 'None'
                                    }
                                ]}
                            />
                        </ColumnLayout>
                    </Box>
                )}

                <Form
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            {config && (
                                <Button
                                    variant="normal"
                                    onClick={() => setDeleteModalVisible(true)}
                                    disabled={saving || deleting}
                                >
                                    Delete configuration
                                </Button>
                            )}
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                loading={saving}
                                disabled={deleting}
                            >
                                Save configuration
                            </Button>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween size="l">
                        <FormField
                            label="Webhook URL"
                            description="The endpoint where notifications will be sent (e.g. https://your-domain.com/webhook)."
                            constraintText="Must be a valid HTTPS URL."
                        >
                            <Input
                                value={url}
                                onChange={({ detail }) => setUrl(detail.value)}
                                placeholder="https://example.com/webhook"
                                disabled={saving || deleting}
                                type="url"
                            />
                        </FormField>

                        <FormField
                            label="Custom headers (optional)"
                            description='Provide custom headers as JSON, e.g. {"Authorization": "Bearer YOUR_TOKEN", "X-Custom-Header": "value"}.'
                            errorText={headersError || undefined}
                        >
                            <Textarea
                                value={headersJson}
                                onChange={({ detail }) => handleHeadersChange(detail.value)}
                                onBlur={() => {
                                    // J2: validate JSON on blur and show the parser error in the FormField.
                                    if (headersJson.trim()) {
                                        validateAndParseHeaders(headersJson);
                                    }
                                }}
                                placeholder='{"Authorization": "Bearer YOUR_TOKEN"}'
                                rows={8}
                                disabled={saving || deleting}
                            />
                        </FormField>

                        <Alert type="info">
                            <strong>How it works:</strong>
                            <ul>
                                <li>When a trading action occurs, a POST request is sent to your webhook URL.</li>
                                <li>The request body contains details about the action.</li>
                                <li>Custom headers are included for authentication.</li>
                                <li>Make sure your endpoint is publicly accessible and can handle POST requests.</li>
                            </ul>
                        </Alert>
                    </SpaceBetween>
                </Form>

                <Modal
                    visible={deleteModalVisible}
                    onDismiss={() => setDeleteModalVisible(false)}
                    header="Delete webhook configuration"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    variant="link"
                                    onClick={() => setDeleteModalVisible(false)}
                                    disabled={deleting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleDelete}
                                    loading={deleting}
                                >
                                    Delete
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween size="m">
                        <Box>
                            Are you sure you want to delete your webhook configuration? This action cannot be undone.
                        </Box>
                        {config && (
                            <Alert type="warning">
                                You are about to delete the webhook URL: <strong>{config.url}</strong>
                            </Alert>
                        )}
                    </SpaceBetween>
                </Modal>

                <ExpandableSection
                    headerText="Test article processing"
                    variant="container"
                    defaultExpanded={false}
                >
                    <SpaceBetween size="l">
                        <Alert type="info">
                            <strong>Test your article processing configuration:</strong>
                            <ul>
                                <li><strong>Test Article:</strong> Process an article and see the response directly in your browser.</li>
                                <li><strong>Send to webhook:</strong> Process an article and send the result to your configured webhook URL.</li>
                            </ul>
                        </Alert>

                        {testAlert && (
                            <Alert
                                type={testAlert.type}
                                dismissible
                                onDismiss={() => setTestAlert(null)}
                            >
                                {testAlert.message}
                            </Alert>
                        )}

                        <Form
                            actions={
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button
                                        variant="normal"
                                        onClick={handleTestArticle}
                                        loading={testingArticle}
                                        disabled={!testArticleId || !testCredentialKey || sendingToCallback}
                                    >
                                        Test Article
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleSendToCallback}
                                        loading={sendingToCallback}
                                        disabled={!testArticleId || !testCredentialKey || testingArticle || !config?.url}
                                    >
                                        Send to webhook
                                    </Button>
                                </SpaceBetween>
                            }
                        >
                            <SpaceBetween size="l">
                                <FormField
                                    label="Article ID"
                                    description="Enter the Seeking Alpha article ID to process (e.g., 4747895)"
                                >
                                    <Input
                                        value={testArticleId}
                                        onChange={({ detail }) => setTestArticleId(detail.value)}
                                        placeholder="4747895"
                                        disabled={testingArticle || sendingToCallback}
                                    />
                                </FormField>

                                <FormField
                                    label="Credential"
                                    description="Select which Seeking Alpha credential to use for fetching the article"
                                >
                                    <Select
                                        selectedOption={testCredentialKey}
                                        onChange={({ detail }) => setTestCredentialKey(detail.selectedOption)}
                                        options={credentialKeyOptions}
                                        placeholder="Choose a credential"
                                        disabled={testingArticle || sendingToCallback}
                                    />
                                </FormField>
                            </SpaceBetween>
                        </Form>

                        {testResult && (
                            <Box>
                                <Header variant="h3">Test Result (JSON Response)</Header>
                                <Box padding={{ top: 's' }}>
                                    <SpaceBetween size="m">
                                        <Alert type="info">
                                            <strong>Response includes:</strong>
                                            <ul>
                                                <li>Article ID and Title</li>
                                                <li>Buy and Sell Tickers</li>
                                                <li>Ticker Validation Method</li>
                                                <li>Market Cap Data for each ticker</li>
                                                <li>Portfolio (SeekingAlphaPro or SeekingAlphaPremium)</li>
                                            </ul>
                                        </Alert>
                                        <pre style={{
                                            backgroundColor: '#f4f4f4',
                                            padding: '16px',
                                            borderRadius: '4px',
                                            overflow: 'auto',
                                            maxHeight: '500px',
                                            fontSize: '13px',
                                            lineHeight: '1.5',
                                            border: '1px solid #d5dbdb'
                                        }}>
                                            {JSON.stringify(testResult, null, 2)}
                                        </pre>
                                    </SpaceBetween>
                                </Box>
                            </Box>
                        )}
                    </SpaceBetween>
                </ExpandableSection>
            </SpaceBetween>
        </Container>
    );
}
